import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  Home,
  CalendarRange,
  List,
  Music,
  ListMusic,
  ListOrdered,
  Users,
  UserCircle,
  MapPin,
  Bed,
  Briefcase,
  Wrench,
  BarChart3,
  Settings,
  LayoutDashboard,
  CalendarPlus,
  Search,
  Bell,
  Eye,
  User,
  ChevronLeft,
  ChevronDown,
  LogOut,
  Route,
  Sparkles,
} from 'lucide-react';
import { useUIPreferences } from '@/context/UIPreferencesContext';
import { useAuth } from '@/context/AuthContext';

interface DesktopTopNavProps {
  activeNav: string;
  onNavigate?: (navId: string) => void;
  leftSidebarOpen?: boolean;
  onLeftSidebarToggle?: (open: boolean) => void;
}

interface NavItem {
  id: string;
  name: string;
  href: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

interface NavGroup {
  name: string;
  items: NavItem[];
}

const topLevelItems: NavItem[] = [
  { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { id: 'calendar', name: 'Calendar', href: '/calendar', icon: CalendarRange },
];

const navigationGroups: NavGroup[] = [
  {
    name: 'Live',
    items: [
      { id: 'events', name: 'Events', href: '/events', icon: CalendarPlus },
      { id: 'venues', name: 'Venues', href: '/venues', icon: MapPin },
      { id: 'accommodations', name: 'Stay', href: '/accommodations', icon: Bed },
    ],
  },
  {
    name: 'Music',
    items: [
      { id: 'songs', name: 'Songs', href: '/songs', icon: Music },
      { id: 'setlists', name: 'Setlists', href: '/setlists', icon: ListOrdered },
    ],
  },
  {
    name: 'Team',
    items: [
      { id: 'collaborators', name: 'Collaborators', href: '/collaborators', icon: Users },
      { id: 'ensembles', name: 'Ensembles', href: '/ensembles', icon: UserCircle },
    ],
  },
  {
    name: 'Tools',
    items: [
      { id: 'riders', name: 'Riders', href: '/riders', icon: Briefcase },
      { id: 'equipment', name: 'Equipment', href: '/equipment', icon: Wrench },
      { id: 'analytics', name: 'Analytics', href: '/analytics', icon: BarChart3 },
      { id: 'settings', name: 'Settings', href: '/settings', icon: Settings },
    ],
  },
  {
    name: 'AI',
    items: [
      { id: 'ai-tour-planning', name: 'Tour Planning', href: '/ai/tour-planning', icon: Route },
    ],
  },
];

export function DesktopTopNav({
  activeNav,
  onNavigate: _onNavigate,
  leftSidebarOpen = false,
  onLeftSidebarToggle,
}: DesktopTopNavProps) {
  void _onNavigate;
  const location = useLocation();
  const { showPageSummary, updatePreferences } = useUIPreferences();
  const { logout } = useAuth();
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userMenuOpen]);

  const isActive = (href: string) => {
    return location.pathname === href ||
      (href !== '/' && location.pathname.startsWith(href));
  };

  const toggleGroup = (groupName: string) => {
    setOpenGroup(openGroup === groupName ? null : groupName);
  };

  const handleLinkClick = () => {
    setOpenGroup(null);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-[100] bg-white border-b border-gray-200 shadow-sm">
      <div className="flex items-center justify-between h-16 px-3 md:px-4">
        <div className="flex items-center space-x-2 md:space-x-4">
          <button
            onClick={() => onLeftSidebarToggle?.(!leftSidebarOpen)}
            className="p-1 rounded transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
            title="Toggle sidebar"
          >
            {leftSidebarOpen ? (
              <ChevronLeft className="w-4 h-4" />
            ) : (
              <Menu className="w-4 h-4" />
            )}
          </button>

          <Link to="/dashboard" className="flex items-center">
            <span className="text-xl font-bold text-primary-600">Luma</span>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {topLevelItems.map((item) => {
              const Icon = item.icon;
              const active = activeNav === item.id || isActive(item.href);
              return (
                <Link
                  key={item.id}
                  to={item.href}
                  className={`
                    flex items-center px-3 py-2 rounded-lg text-sm font-medium
                    transition-colors
                    ${
                      active
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }
                  `}
                >
                  {Icon ? <Icon className="w-4 h-4 mr-2" /> : null}
                  {item.name}
                </Link>
              );
            })}
          </nav>

          <nav className="hidden lg:flex items-center space-x-1">
            {navigationGroups.map((group) => (
              <div key={group.name} className="relative">
                <button
                  onClick={() => toggleGroup(group.name)}
                  className={`
                    flex items-center px-3 py-2 rounded-lg text-sm font-medium
                    transition-colors
                    ${
                      group.items.some(item => activeNav === item.id || isActive(item.href))
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }
                  `}
                >
                  <span>{group.name}</span>
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>

                {openGroup === group.name && (
                  <div className="absolute top-full mt-1 left-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                    {group.items.map((item) => {
                      const active = activeNav === item.id || isActive(item.href);
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.id}
                          to={item.href}
                          onClick={handleLinkClick}
                          className={`
                            flex items-center px-4 py-3 text-sm font-medium
                            transition-colors min-h-[44px]
                            ${
                              active
                                ? 'bg-primary-100 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-100'
                            }
                          `}
                        >
                          {Icon ? <Icon className="w-4 h-4 mr-3 text-gray-400" /> : null}
                          <span>{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

        <div className="flex items-center space-x-4">
          <div className="hidden md:block">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm w-48"
              />
            </div>
          </div>

          <button
            className="relative p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 min-w-[40px] min-h-[40px] flex items-center justify-center"
            title="Notifications"
          >
            <Bell className="w-5 h-5" />
          </button>

          <button
            onClick={() => updatePreferences({ showPageSummary: !showPageSummary })}
            className={`
              p-1 rounded transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center
              ${
                showPageSummary
                  ? 'bg-primary-100 text-primary-600'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }
            `}
            title={showPageSummary ? 'Hide page summaries' : 'Show page summaries'}
          >
            <Eye className="w-4 h-4" />
          </button>

          <button
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 min-w-[40px] min-h-[40px] flex items-center justify-center"
            title="Profile"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <User className="w-5 h-5" />
          </button>

          {userMenuOpen && (
            <div
              ref={userMenuRef}
              className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]"
            >
              <Link
                to="/settings"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 min-h-[44px]"
              >
                <Settings className="w-4 h-4 mr-3 text-gray-400" />
                Settings
              </Link>
              <Link
                to="/settings/profile"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 min-h-[44px]"
              >
                <User className="w-4 h-4 mr-3 text-gray-400" />
                Profile
              </Link>
              <hr className="my-1" />
              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  logout();
                }}
                className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 min-h-[44px]"
              >
                <LogOut className="w-4 h-4 mr-3 text-gray-400" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default DesktopTopNav;
